import { toast as sonnerToast } from "sonner";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastOptions {
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

/**
 * Utilitaires pour afficher des toasts améliorés
 * Utilise Sonner avec des styles cohérents
 */
export const toast = {
  success: (message: string, options?: ToastOptions) => {
    sonnerToast.success(message, {
      description: options?.description,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick
      } : undefined,
      duration: options?.duration ?? 4000,
    });
  },

  error: (message: string, options?: ToastOptions) => {
    sonnerToast.error(message, {
      description: options?.description,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick
      } : undefined,
      duration: options?.duration ?? 5000,
    });
  },

  warning: (message: string, options?: ToastOptions) => {
    sonnerToast.warning(message, {
      description: options?.description,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick
      } : undefined,
      duration: options?.duration ?? 5000,
    });
  },

  info: (message: string, options?: ToastOptions) => {
    sonnerToast.info(message, {
      description: options?.description,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick
      } : undefined,
      duration: options?.duration ?? 4000,
    });
  },

  // Toast avec action de retry
  errorWithRetry: (message: string, onRetry: () => void, description?: string) => {
    sonnerToast.error(message, {
      description,
      action: {
        label: "Réessayer",
        onClick: onRetry
      },
      duration: 6000,
    });
  },

  // Toast pour les erreurs CRUD
  crudError: (action: "création" | "modification" | "suppression" | "chargement", entity: string, onRetry?: () => void) => {
    const message = `Erreur lors de la ${action}`;
    const description = `Impossible de ${action === "chargement" ? "charger" : action.slice(0, -3) + "er"} ${entity}`;

    if (onRetry) {
      sonnerToast.error(message, {
        description,
        action: {
          label: "Réessayer",
          onClick: onRetry
        },
        duration: 6000,
      });
    } else {
      sonnerToast.error(message, {
        description,
        duration: 5000,
      });
    }
  },

  // Toast pour les erreurs OCR
  ocrError: (reason: "format" | "taille" | "lecture" | "generic", onRetry?: () => void) => {
    const messages: Record<string, { title: string; description: string }> = {
      format: {
        title: "Format non supporté",
        description: "Seuls les formats JPG, PNG et PDF sont acceptés"
      },
      taille: {
        title: "Fichier trop volumineux",
        description: "La taille maximale est de 10 Mo"
      },
      lecture: {
        title: "Impossible de lire le document",
        description: "Le document est peut-être illisible ou endommagé"
      },
      generic: {
        title: "Erreur d'analyse",
        description: "Le Scan IA n'a pas pu analyser ce document"
      }
    };

    const { title, description } = messages[reason] || messages.generic;

    if (onRetry) {
      sonnerToast.error(title, {
        description,
        action: {
          label: "Réessayer",
          onClick: onRetry
        },
        duration: 6000,
      });
    } else {
      sonnerToast.error(title, {
        description,
        duration: 5000,
      });
    }
  },

  // Toast pour cooldown
  cooldown: (minutes: number) => {
    sonnerToast.warning("Action temporairement indisponible", {
      description: `Veuillez patienter ${minutes} minute${minutes > 1 ? "s" : ""} avant de réessayer`,
      duration: 4000,
    });
  },

  // Toast de succès CRUD
  crudSuccess: (action: "créé" | "modifié" | "supprimé", entity: string) => {
    sonnerToast.success(`${entity} ${action} avec succès`, {
      duration: 3000,
    });
  },

  // Promise toast pour les opérations async
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    }
  ) => {
    return sonnerToast.promise(promise, messages);
  }
};
